import { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Card, CardContent, Button, Chip, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { analyzeNutrition, NutritionAnalysis, getCachedNutritionAnalysis } from '../services/nutritionService';
import NutritionStats from '../components/NutritionStats';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import { useAnalysisPending } from '../context/AnalysisPendingContext';
import { useHistoryRefresh } from '../context/HistoryRefreshContext';
import { useFoodHistory } from '../context/FoodHistoryContext';
import FeedbackModal from '../components/FeedbackModal';

export default function Home() {
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState<NutritionAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const { analysisPending, setAnalysisPending } = useAnalysisPending();
  const { historyNeedsRefresh, setHistoryNeedsRefresh } = useHistoryRefresh();
  const { refreshHistory } = useFoodHistory();
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);

  // Log para depuración
  console.log('🏠 Home component render - Estado actual:', {
    analysis: analysis ? '✅ Presente' : '❌ Null',
    loading,
    error,
    analysisPending,
    isGeneratingAnalysis,
    showProgressModal,
    progress,
    userId: user?.id
  });

  // Verificar si el usuario está autenticado
  if (!user?.id) {
    console.log('⚠️ Usuario no autenticado, redirigiendo a login');
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // Log cuando el usuario cambia
  useEffect(() => {
    console.log('👤 Usuario detectado:', user);
  }, [user]);

  // Log del ciclo de vida del componente
  useEffect(() => {
    console.log('🏠 Home component MOUNTED');
    return () => {
      console.log('🏠 Home component UNMOUNTED');
    };
  }, []);

  // Verificar si el usuario está completamente cargado
  useEffect(() => {
    if (user?.id) {
      console.log('✅ Usuario completamente cargado:', user.id);
    } else {
      console.log('⏳ Usuario aún no cargado o no autenticado');
    }
  }, [user]);

  // Rastrear cambios en el análisis
  useEffect(() => {
    console.log('🔄 ANÁLISIS CAMBIÓ:', {
      analysis: analysis ? '✅ Presente' : '❌ Null',
      tieneStats: analysis?.stats ? '✅' : '❌',
      totalEntries: analysis?.stats?.totalEntries,
      timestamp: new Date().toISOString()
    });
  }, [analysis]);

  // Rastrear cambios en showProgressModal
  useEffect(() => {
    console.log('🎭 SHOW_PROGRESS_MODAL CAMBIÓ:', {
      showProgressModal,
      loading,
      isGeneratingAnalysis,
      timestamp: new Date().toISOString()
    });
  }, [showProgressModal, loading, isGeneratingAnalysis]);

  const loadCachedAnalysis = async () => {
    console.log('🚀 INICIO loadCachedAnalysis - Usuario:', user?.id);
    if (!user?.id) {
      console.log('❌ No hay usuario, saliendo de loadCachedAnalysis');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 Cargando análisis cacheado para usuario:', user.id);
      const result = await getCachedNutritionAnalysis(user.id);
      console.log('📊 Resultado del servicio getCachedNutritionAnalysis:', result);
      console.log('📊 Tipo del resultado:', typeof result);
      console.log('📊 ¿Es objeto?', typeof result === 'object');
      console.log('📊 ¿Tiene structuredAnalysis?', result && 'structuredAnalysis' in result);
      console.log('📊 ¿Tiene stats?', result && 'stats' in result);
      
      if (
        result &&
        typeof result === 'object' &&
        'structuredAnalysis' in result &&
        'stats' in result
      ) {
        console.log('✅ Estructura correcta encontrada, estableciendo análisis');
        console.log('📊 Análisis antes de setAnalysis:', result);
        setAnalysis(result);
        console.log('✅ setAnalysis ejecutado');
      } else {
        console.log('❌ Estructura incorrecta o resultado null, estableciendo analysis como null');
        setAnalysis(null);
        console.warn('No se encontró análisis en la respuesta del backend');
      }
    } catch (err: any) {
      if (err.message === 'NO_CACHED_ANALYSIS') {
        console.log('ℹ️ No hay análisis cacheado, esto es normal en usuarios nuevos');
        setAnalysis(null);
      } else {
        console.error('Error obteniendo análisis cacheado:', err);
        setErrorModalMessage('Error obteniendo el análisis cacheado. Intenta de nuevo.');
        setErrorModalOpen(true);
        setAnalysis(null);
      }
    } finally {
      setLoading(false);
      console.log('🏁 FIN loadCachedAnalysis');
    }
  };

  const generateNewAnalysis = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 Generando nuevo análisis nutricional...');
      const result = await analyzeNutrition(user.id, user);
      console.log('✅ Análisis generado:', result);
      setAnalysis(result);
    } catch (err: any) {
      console.error('Error generando análisis:', err);
      setErrorModalMessage('Error al generar el análisis nutricional. Por favor, intenta de nuevo.');
      setErrorModalOpen(true);
      setError('Error al generar el análisis nutricional. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 useEffect ejecutándose:', {
      analysisPending,
      isGeneratingAnalysis,
      userId: user?.id
    });
    
    if (analysisPending && !isGeneratingAnalysis) {
      console.log('🚀 Generando nuevo análisis...');
      setIsGeneratingAnalysis(true);
      setShowProgressModal(true);
      setProgress(0);
      setAnalysis(null);
      (async () => {
        try {
          await generateNewAnalysis();
        } finally {
          setAnalysisPending(false);
          setShowProgressModal(false);
          setIsGeneratingAnalysis(false);
        }
      })();
      return;
    }
    
    if (!analysisPending && !isGeneratingAnalysis) {
      console.log('📥 Cargando análisis cacheado...');
      loadCachedAnalysis();
    }
  }, [user?.id, analysisPending, isGeneratingAnalysis]);

  // Animar barra de progreso más realista
  useEffect(() => {
    if (showProgressModal) {
      const interval = setInterval(() => {
        setProgress((old) => {
          // Hacer que el progreso sea más lento y realista
          if (old >= 95) {
            // Mantener en 95% hasta que se complete el análisis
            return 95;
          }
          // Progreso más lento y natural
          return old + Math.random() * 3 + 1;
        });
      }, 200); // Más lento para que dure más tiempo
      return () => clearInterval(interval);
    }
  }, [showProgressModal]);

  // Cerrar modal cuando haya análisis
  useEffect(() => {
    if (showProgressModal && analysis) {
      // Llegar al 100% antes de cerrar
      setProgress(100);
      // Esperar un momento para que se vea el 100% antes de cerrar
      setTimeout(() => {
        setShowProgressModal(false);
      }, 500);
    }
  }, [showProgressModal, analysis]);

  useEffect(() => {
    if (historyNeedsRefresh) {
      refreshHistory();
      setHistoryNeedsRefresh(false);
    }
  }, [historyNeedsRefresh, refreshHistory, setHistoryNeedsRefresh]);

  if (loading && !showProgressModal) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // Si el modal de progreso está activo, mostrarlo primero
  if (showProgressModal) {
    console.log('🎭 Renderizando modal de progreso');
    return (
      <>
        <Modal open={showProgressModal} aria-labelledby="modal-progreso-analisis" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ 
            bgcolor: 'white', 
            p: 4, 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)', 
            minWidth: 320, 
            maxWidth: 400,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'primary.light',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Fondo decorativo */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              bgcolor: 'primary.main',
              borderRadius: '3px 3px 0 0'
            }} />
            
            {/* Contenedor de la imagen con animación mejorada */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              position: 'relative'
            }}>
              <Box sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.05)', opacity: 0.8 },
                  '100%': { transform: 'scale(1)', opacity: 1 }
                }
              }}>
                <img 
                  src="/imgs_ayuda_img_ok.jpg" 
                  alt="Verdura animada" 
                  style={{ 
                    width: 60, 
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: '50%',
                    animation: 'bounce 1.5s ease-in-out infinite'
                  }} 
                />
              </Box>
            </Box>
            
            <Typography variant="h6" mb={2} color="primary.main" fontWeight={600}>
              Analizando tus vegetales...
            </Typography>
            
            <Typography variant="body2" color="text.secondary" mb={3}>
              Nuestra IA está procesando tu plato para darte el mejor análisis nutricional
            </Typography>
            
            {/* Barra de progreso mejorada */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mb: 1,
                fontSize: '0.875rem',
                color: 'text.secondary'
              }}>
                <span>Procesando...</span>
                <span>{Math.round(progress)}%</span>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4, 
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': { 
                    bgcolor: 'primary.main', 
                    borderRadius: 4,
                    transition: 'transform 0.3s ease'
                  } 
                }} 
              />
            </Box>
            
            <Typography variant="caption" color="text.secondary">
              Esto puede tardar unos segundos
            </Typography>
          </Box>
        </Modal>
        {/* Renderizar el contenido normal detrás del modal */}
        <Box sx={{ opacity: 0.3, pointerEvents: 'none' }}>
          {/* Aquí iría el contenido normal pero con opacidad reducida */}
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          fullWidth 
          variant="contained" 
          onClick={loadCachedAnalysis}
          sx={{ borderRadius: 999, fontWeight: 600 }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  // Presentación para primer acceso (sin platos subidos)
  if (!analysis || (analysis.stats && analysis.stats.totalEntries === 0)) {
    console.log('Render: Página de bienvenida. analysis:', analysis);
    console.log('🔍 Detalles del análisis:', {
      analysisExiste: !!analysis,
      statsExiste: !!analysis?.stats,
      totalEntries: analysis?.stats?.totalEntries,
      condicion1: !analysis,
      condicion2: analysis?.stats && analysis.stats.totalEntries === 0
    });
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" textAlign="center">
        <EmojiEventsIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" fontWeight={700} color="primary.main" mb={2}>
          ¡Bienvenido a GreenBalance!
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={2}>
          GreenBalance es tu asistente nutricional inteligente. Analiza tus comidas, te motiva a mejorar tu alimentación y te da consejos personalizados cada semana.
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={2}>
          <b>¿Cómo funciona?</b> Sube fotos de tus platos o regístralos manualmente. La app analizará tus hábitos y te dará recomendaciones para que consigas tus objetivos de salud.
        </Typography>
        <Typography variant="body1" color="primary.main" fontWeight={600} mb={3}>
          ¡Empieza ahora subiendo tu primer plato y descubre tu potencial saludable!
        </Typography>
        <RestaurantIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 2 }} />
        <Button 
          fullWidth 
          variant="contained" 
          color="primary"
          sx={{ borderRadius: 999, fontWeight: 600, fontSize: '1.1rem', mt: 2 }}
          onClick={() => navigate('/register')}
        >
          Subir mi primer plato
        </Button>
      </Box>
    );
  }

  // Mensaje motivador si hay solo una comida
  if (analysis && analysis.stats && analysis.stats.totalEntries === 1) {
    console.log('Render: Usuario con una sola comida. analysis:', analysis);
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" textAlign="center">
        <EmojiEventsIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" fontWeight={600} mb={2} color="primary.main">
          ¡Genial! Has registrado tu primera comida 🍽️
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={2}>
          Sigue subiendo tus platos para recibir un análisis nutricional más completo y recomendaciones personalizadas. ¡Cuantos más datos, mejores consejos!
        </Typography>
        <Button 
          fullWidth 
          size="large" 
          variant="contained" 
          color="primary" 
          sx={{ borderRadius: 999, fontWeight: 600, fontSize: '1.1rem', mb: 2 }}
          onClick={() => navigate('/register')}
        >
          Añadir otro plato
        </Button>
        <Button 
          fullWidth 
          size="large" 
          variant="outlined" 
          color="secondary" 
          sx={{ borderRadius: 999, fontWeight: 600, fontSize: '1.1rem', mb: 3 }}
          onClick={generateNewAnalysis}
          disabled={loading}
        >
          {loading ? 'Generando análisis...' : 'Generar análisis nutricional'}
        </Button>
        <NutritionStats stats={{ ...analysis.stats, startDate: (analysis && 'period_start' in analysis ? (analysis as any).period_start : undefined) }} />
      </Box>
    );
  }

  // Mensaje motivador si hay pocos datos (primeras comidas)
  if (analysis && analysis.stats && analysis.stats.totalEntries > 1 && analysis.stats.totalEntries < 3) {
    console.log('Render: Usuario con pocas comidas. analysis:', analysis);
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} mb={2} color="primary.main">
          ¡Genial! Ya has empezado a registrar tus comidas 🍽️
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={2}>
          Sigue subiendo tus platos para recibir un análisis nutricional más completo y recomendaciones personalizadas.
        </Typography>
        <Button 
          fullWidth 
          size="large" 
          variant="contained" 
          color="primary" 
          sx={{ borderRadius: 999, fontWeight: 600, fontSize: '1.1rem', mb: 2 }}
          onClick={() => navigate('/register')}
        >
          Añadir otro plato
        </Button>
        <Button 
          fullWidth 
          size="large" 
          variant="outlined" 
          color="secondary" 
          sx={{ borderRadius: 999, fontWeight: 600, fontSize: '1.1rem', mb: 3 }}
          onClick={generateNewAnalysis}
          disabled={loading}
        >
          {loading ? 'Generando análisis...' : 'Generar análisis nutricional'}
        </Button>
        <NutritionStats stats={{ ...analysis.stats, startDate: (analysis && 'period_start' in analysis ? (analysis as any).period_start : undefined) }} />
      </Box>
    );
  }

  // Vista normal con análisis completo
  console.log('Render: Vista normal con análisis completo. analysis:', analysis);
  
  const { structuredAnalysis, stats } = analysis;
  // Compatibilidad: claves en español o inglés
  const sa: any = structuredAnalysis;
  const progreso = sa.progreso || sa.progress;
  const missingNutrients = sa.nutrientes_faltantes || sa.missingNutrients || [];
  const suggestions = sa.sugerencias || sa.suggestions || [];
  const analisisTexto = sa.analisis || sa.analysis;

  if (errorModalOpen) {
    return (
      <FeedbackModal
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title="¡Ha ocurrido un error!"
        message={errorModalMessage}
        buttonText="Cerrar"
        type="error"
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={2} color="primary.main">
        Has consumido {progreso.consumidos ?? progreso.consumed} de {progreso.objetivo ?? progreso.total} vegetales esta semana
      </Typography>
      <LinearProgress 
        variant="determinate" 
        value={progreso.porcentaje ?? progreso.percentage} 
        sx={{ 
          height: 10, 
          borderRadius: 5, 
          mb: 3, 
          bgcolor: 'secondary.light', 
          '& .MuiLinearProgress-bar': { 
            bgcolor: 'primary.main', 
            borderRadius: 5 
          } 
        }} 
      />
      {/* Card bonito de análisis motivador */}
      {analisisTexto && (
        (() => { console.log('ANÁLISIS TEXTO:', analisisTexto); return null; })()
      )}
      {analisisTexto && (
        <Card sx={{ mb: 3, borderRadius: 4, borderColor: 'primary.main', boxShadow: 'none' }}>
          <CardContent>
            <Typography variant="body1" color="primary.main" fontWeight={600} mb={1}>
              Análisis semanal
            </Typography>
            <Typography color="text.secondary">
              {analisisTexto}
            </Typography>
          </CardContent>
        </Card>
      )}
      {suggestions.length > 0 && (
        <Card sx={{ mb: 2, borderRadius: 4, border: '2px solid', borderColor: 'secondary.main', boxShadow: 'none' }}>
          <CardContent>
            <Typography variant="body1" fontWeight={500} color="primary.main" mb={1}>
              Sugerencias
            </Typography>
            <ul style={{ color: '#f57c00', margin: 0, paddingLeft: '1.2em' }}>
              {suggestions.map((s: string, i: number) => (
                <li key={i} style={{ marginBottom: 4 }}>{s}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      <NutritionStats stats={{ ...stats, startDate: (analysis && 'period_start' in analysis ? (analysis as any).period_start : undefined) }} />
      <Button 
        fullWidth 
        size="large" 
        variant="contained" 
        color="primary" 
        sx={{ borderRadius: 999, fontWeight: 600, fontSize: '1.1rem', mt: 2 }}
        onClick={() => navigate('/register')}
      >
        Añadir más platos
      </Button>
    </Box>
  );
} 